import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { useActionsPortefeuille, usePortefeuille, useStructures } from '@/api/hooks';
import { LIBELLE_STATUT_PRET_ACCORDE, type StatutPretAccorde } from '@/api/types';
import { useSession } from '@/auth/session';
import { BoutonExcel, Chargement, ErreurBloc, Kpi, PageHeader, Vide } from '@/components/common';
import { montantDevise, StatutPretAccordeBadge } from '@/components/rh';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { dateFr } from '@/lib/utils';

const STATUTS = Object.keys(LIBELLE_STATUT_PRET_ACCORDE) as StatutPretAccorde[];
const usd = (n: number) => montantDevise(n, 'USD');

/** Portefeuille des prêts (§ 5.9 et 5.11) : indicateurs, rendement par banque, liste des prêts accordés, exports, reprise. */
export function Portefeuille() {
  const u = useSession()!.utilisateur;
  const [statut, setStatut] = useState<StatutPretAccorde | ''>('');
  const [structure, setStructure] = useState('');
  const [banque, setBanque] = useState('');
  const structures = useStructures();
  const { data, isLoading, error } = usePortefeuille({ statut: statut || undefined, structure: structure || undefined, banque: banque || undefined });
  const [reprise, setReprise] = useState(false);
  const peutReprendre = u.profils.some((p) => ['DIRECTEUR_RH', 'RESSOURCES_HUMAINES', 'INVESTISSEMENTS', 'DIRECTEUR_FINANCIER'].includes(p));
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const i = data.indicateurs;
  return (
    <div>
      <PageHeader
        titre="Portefeuille des prêts sociaux"
        sousTitre="Demandes, montants, encours, intérêts, échéances non retenues, créances en recouvrement, rendement par banque ; liste des prêts accordés."
        actions={
          <div className="flex flex-wrap gap-2">
            <BoutonExcel chemin={`/rh/portefeuille.xlsx${statut ? `?statut=${statut}` : ''}`} nom="portefeuille-prets.xlsx" libelle="Exporter (Excel)" taille="default" />
            {peutReprendre && (
              <Button variant="outline" onClick={() => setReprise(true)}>
                Reprise des prêts en cours
              </Button>
            )}
          </div>
        }
      />
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="indicateurs-prets">
        <Kpi label="Prêts en force" valeur={i.pretsEnForce} detail={`${i.aDecaisser} à décaisser`} />
        <Kpi label="Capital restant dû" valeur={usd(i.capitalRestantDu)} detail={`décaissé ${usd(i.montantsDecaisses.total)} (net ${usd(i.montantsDecaisses.net)}, rachats ${usd(i.montantsDecaisses.rachat)})`} />
        <Kpi label="Intérêts encaissés" valeur={usd(i.interetsEncaisses)} detail={`échus ${usd(i.interetsEchus)} · prévisionnels ${usd(i.interetsPrevisionnels)}`} />
        <Kpi label="Échéances non retenues" valeur={i.echeancesNonRetenues} detail={`${i.creancesEnRecouvrement.nombre} créance(s) en recouvrement · ${usd(i.creancesEnRecouvrement.montant)}`} accent={i.echeancesNonRetenues > 0 || i.creancesEnRecouvrement.nombre > 0 ? 'alerte' : 'ok'} />
        <Kpi label="Demandes" valeur={Object.values(i.demandes).reduce((t, n) => t + n, 0)} detail={Object.entries(i.demandes).map(([s, n]) => `${n} ${s.toLowerCase().replace(/_/g, ' ')}`).join(' · ') || 'aucune'} />
        <Kpi label="Montants demandés / approuvés" valeur={usd(i.montantsApprouves)} detail={`demandés ${usd(i.montantsDemandes)}`} />
        <Kpi label="Délai moyen de décision" valeur={i.delaiMoyenJours === null ? '—' : `${i.delaiMoyenJours} j`} detail="de la réception par la DF à la décision finale" />
        <Kpi label="Taux d'approbation" valeur={i.tauxApprobation === null ? '—' : `${i.tauxApprobation} %`} detail={i.tauxRejet === null ? '' : `rejet ${i.tauxRejet} %`} />
      </div>
      {i.rendement.length > 0 && (
        <Card className="mb-4" data-testid="rendement">
          <CardHeader>
            <CardTitle>Rendement par banque ou compte de décaissement</CardTitle>
            <CardDescription>Les intérêts à collecter sont prévisionnels ; seuls les intérêts encaissés sont constatés.</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Banque / compte</TableHead>
                  <TableHead className="text-right">Prêts en force</TableHead>
                  <TableHead className="text-right">Encours</TableHead>
                  <TableHead className="text-right">Intérêts à collecter</TableHead>
                  <TableHead className="text-right">Intérêts encaissés</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {i.rendement.map((r) => (
                  <TableRow key={r.banque}>
                    <TableCell>{r.banque}</TableCell>
                    <TableCell className="num text-right">{r.pretsEnForce}</TableCell>
                    <TableCell className="num text-right">{usd(r.encours)}</TableCell>
                    <TableCell className="num text-right">{usd(r.interetsACollecter)}</TableCell>
                    <TableCell className="num text-right">{usd(r.interetsEncaisses)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SelectNatif value={statut} onChange={(e) => setStatut(e.target.value as StatutPretAccorde | '')}>
          <option value="">Tous les statuts</option>
          {STATUTS.map((s) => (
            <option key={s} value={s}>
              {LIBELLE_STATUT_PRET_ACCORDE[s]}
            </option>
          ))}
        </SelectNatif>
        <SelectNatif value={structure} onChange={(e) => setStructure(e.target.value)}>
          <option value="">Toutes les structures</option>
          {(structures.data ?? []).map((s) => (
            <option key={s.code} value={s.code}>
              {s.code} · {s.libelle}
            </option>
          ))}
        </SelectNatif>
        <Input className="w-56" placeholder="Banque ou compte…" value={banque} onChange={(e) => setBanque(e.target.value)} />
      </div>
      {!data.prets.length ? (
        <Vide message="Aucun prêt accordé." />
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Prêt</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead className="hidden md:table-cell">Banque</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead className="text-right">Capital restant dû</TableHead>
                <TableHead className="hidden lg:table-cell">Prochaine échéance</TableHead>
                <TableHead className="text-right hidden sm:table-cell">Non retenues</TableHead>
                <TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.prets.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>
                    <Link to={p.demandeId ? `/rh/prets/${p.demandeId}?onglet=remboursement` : `/rh/portefeuille/${p.id}`} className="font-medium text-primary hover:underline">
                      {p.numero}
                    </Link>
                    <div className="text-xs text-muted-foreground">{p.dateDecaissement ? `décaissé le ${dateFr(p.dateDecaissement)}` : 'à décaisser'}</div>
                  </TableCell>
                  <TableCell>
                    {p.agent.nom}
                    <div className="text-xs text-muted-foreground">{p.structure.code}</div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{p.banque ?? '—'}</TableCell>
                  <TableCell className="num text-right">
                    {montantDevise(p.montantTotal, p.devise)}
                    <div className="text-xs text-muted-foreground">
                      {p.dureeMois} mois · {montantDevise(p.mensualite, p.devise)}
                    </div>
                  </TableCell>
                  <TableCell className="num text-right">{montantDevise(p.solde.capitalRestant, p.devise)}</TableCell>
                  <TableCell className="hidden lg:table-cell">{p.solde.prochaineEcheance ? `${dateFr(p.solde.prochaineEcheance.date)} · ${montantDevise(p.solde.prochaineEcheance.mensualite, p.devise)}` : '—'}</TableCell>
                  <TableCell className="num text-right hidden sm:table-cell">{p.solde.nonRetenues}</TableCell>
                  <TableCell>
                    <StatutPretAccordeBadge statut={p.statut} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <RepriseDialog ouvert={reprise} onClose={() => setReprise(false)} />
    </div>
  );
}

function RepriseDialog({ ouvert, onClose }: { ouvert: boolean; onClose: () => void }) {
  const actions = useActionsPortefeuille();
  const [texte, setTexte] = useState('');
  const [rapport, setRapport] = useState<{ erreurs: Array<{ ligne: number; message: string }>; lignes: number; ecrites: number; ecarts: Array<{ ligne: number; numero: string; message: string }> } | null>(null);
  const lignes = () =>
    texte
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => {
        const [matricule, numero, nature, devise, montantTotal, montantRachat, tauxAnnuel, dureeMois, mensualite, banque, referencePaiement, dateDecaissement, premiereEcheance, remboursements] = l.split(';').map((x) => x.trim());
        return {
          matricule,
          numero: numero || undefined,
          nature: nature || 'CONSOMMATION',
          devise: devise || 'USD',
          montantTotal: Number(montantTotal),
          montantRachat: montantRachat ? Number(montantRachat) : undefined,
          tauxAnnuel: tauxAnnuel ? Number(tauxAnnuel) : 8,
          dureeMois: Number(dureeMois),
          mensualite: mensualite ? Number(mensualite) : undefined,
          banque: banque || undefined,
          referencePaiement: referencePaiement || undefined,
          dateDecaissement,
          premiereEcheance: premiereEcheance || undefined,
          remboursements: remboursements
            ? remboursements
                .split(',')
                .map((r) => r.trim())
                .filter(Boolean)
                .map((r) => {
                  const [date, montant] = r.split('=');
                  return { date: date?.trim() ?? '', montant: Number(montant) };
                })
            : [],
        };
      });
  const lancer = async (simulation: boolean) => {
    try {
      const r = await actions.reprisePrets.mutateAsync({ lignes: lignes(), simulation });
      setRapport(r);
      if (!r.erreurs.length && !simulation) toast.success(`${r.ecrites} prêt(s) repris.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Reprise impossible.');
    }
  };
  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Reprise des prêts en cours</DialogTitle>
          <DialogDescription>Une ligne par prêt, champs séparés par des points-virgules : matricule ; numéro (PS-nnnn, facultatif) ; nature (CONSOMMATION, INVESTISSEMENT) ; devise ; montant total ; encours racheté ; taux annuel ; durée (mois) ; mensualité accordée ; banque ; référence du paiement ; date de décaissement ; première échéance (facultatif) ; remboursements constatés « date=montant, date=montant ». Contrôle tout ou rien ; aucune échéance calculée n'est importée comme remboursement.</DialogDescription>
        </DialogHeader>
        <Textarea rows={8} className="font-mono text-xs" placeholder={'F-0021;PS-0007;CONSOMMATION;USD;2400;0;8;24;108.55;Banque X;OV-2026-0188;2026-07-10;;2026-08-31=108.55,2026-09-30=108.55'} value={texte} onChange={(e) => setTexte(e.target.value)} />
        {rapport && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{rapport.erreurs.length ? `${rapport.erreurs.length} erreur(s)` : rapport.ecrites ? `${rapport.ecrites} prêt(s) repris` : `${rapport.lignes} ligne(s) valide(s), rien d'écrit`}</CardTitle>
              {rapport.ecarts.length > 0 && <CardDescription>{rapport.ecarts.length} écart(s) avec le classeur (mensualité conservée telle qu'accordée).</CardDescription>}
            </CardHeader>
            {(rapport.erreurs.length > 0 || rapport.ecarts.length > 0) && (
              <CardContent className="text-sm">
                {rapport.erreurs.map((e, i) => (
                  <div key={`e${i}`} className="text-destructive">
                    Ligne {e.ligne} : {e.message}
                  </div>
                ))}
                {rapport.ecarts.map((e, i) => (
                  <div key={`x${i}`} className="text-amber-700">
                    Ligne {e.ligne} ({e.numero}) : {e.message}
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => lancer(true)} disabled={!texte.trim() || actions.reprisePrets.isPending}>
            Simuler
          </Button>
          <Button onClick={() => lancer(false)} disabled={!texte.trim() || actions.reprisePrets.isPending}>
            Écrire les prêts
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
